import { BreadcrumbComponent, Button, CIPageHeader, Loader, useToastMessage } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { DropzoneRootProps, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import UploadFiles from '../../../../../common/components/layouts/ImportConfiguration/UploadFiles';
import { EVENT_ROUTE, IMPORT_EVENT, TIMER, URL_PATH } from '../../../../constants';
import { FileType } from '../../../../models/events/IEvent';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { fetchImportRequest } from '../../../../store/actions/sagaActions/events/ImportEventsSagaAction';
import ImportFiles from '../import-files/ImportFiles';
import classes from './ImportEvents.module.scss';

const ImportEvents = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const initialFile: FileType = {
    length: 0,
    lastModified: 0,
    name: '',
    webkitRelativePath: '',
    size: 0,
    type: '',
  };

  const { success: successToast, failure, renderToast, warning } = useToastMessage();
  const [uploadedFilesForJSON, setUploadedFilesForJSON] = useState(initialFile);
  const { success, isLoading, errorMessage } = useSelector((state) => state.postImportEvents);

  const { getRootProps: getRootJSONProps, getInputProps: getInputJSONProps, fileRejections } = useDropzone({
    accept: {
      'application/zip': [IMPORT_EVENT.ZIP_EXT]
    },
    onDrop: (acceptedFile: DropzoneRootProps) => {
      setUploadedFilesForJSON(
        Object.assign(acceptedFile[0], {
          preview: URL.createObjectURL(acceptedFile[0])
        })
      );
    },
    noClick: uploadedFilesForJSON.length !== 0 && !(uploadedFilesForJSON.name?.replace(/.zip/g, '').includes('.'))
  });

  useEffect(() => {
    if (fileRejections.length) {
      warning(
        translate('import_summary_wrapper.error_file_upload_header'),
        translate('import_summary_wrapper.error_file_upload_body'),
        TIMER
      );
    }
  }, [fileRejections]);

  /**
   * @method handleNavigateToImport
   * @description Handles the navigation to the import feature.
   * Uploads the selected files and dispatches a fetch import request.
   */
  const handleNavigateToImport = async () => {
    const formData = new FormData();
     
    const UploadFiles: any = uploadedFilesForJSON;
    formData.append(IMPORT_EVENT.ZIP_FILE, UploadFiles);
    dispatch(fetchImportRequest(formData));
  };

  /**
   * @method closeHandler
   * @description Closes the ImportEvents component and navigates to the event home page.
   */
  const closeHandler = () => {
    navigate(URL_PATH.EVENT_HOME);
  };

  useEffect(() => {
    if (success) {
      successToast(
        translate('toast_component.lbl_success'),
        translate('import_event_configutation.lbl_toast_success_body')
      );
      navigate(EVENT_ROUTE.IMPORT_ROUTE);
      setUploadedFilesForJSON(initialFile);
    }
    if (errorMessage) {
      failure(
        translate('toast_component.lbl_error'),
        errorMessage
      );
    }
  }, [errorMessage, success]);

  return (
    <div className={classes['import-event-configuration-main-container']}>
      <Loader show={isLoading} />
      <BreadcrumbComponent />
      {renderToast()}
      <div className={classes['import-events-button']}>
        <CIPageHeader header={translate('import_summary_wrapper.lbl_import_event_configuration')} />
        {success && <Button onClick={closeHandler} variant="secondary" data-testid="btn-close">{translate('multi_form_modal_component.btn_close')}</Button>}
      </div>
      <p className={classes['import-events-configuration-sub-heading']}>{translate('import_summary_wrapper.subtitle')}</p>
      <Routes>
        <Route
          path="/upload-files"
          element={
            <UploadFiles
              uploadedFilesForJSON={uploadedFilesForJSON}
              getRootJSONProps={getRootJSONProps}
              getInputJSONProps={getInputJSONProps}
              setUploadedFilesForJSON={setUploadedFilesForJSON}
              path={URL_PATH.EVENT_HOME}
              handleNavigateToImport={handleNavigateToImport}
            />
          }
        />
        <Route path="/import" element={<ImportFiles />} />
      </Routes>

    </div>
  );
};

export default ImportEvents;
